import {
  AUTH_TOKEN_COOKIE,
  getAccessToken,
  getApiBaseUrl,
  getCookie,
} from '@/lib/api/client';

/**
 * Error thrown by apiFetch on a non-2xx response. Carries the HTTP status and
 * the parsed response body (JSON when possible, else raw text) so callers can
 * branch on specific responses — e.g. 409 booking conflicts ({ error, conflicts })
 * — without losing information the way a bare Error message would.
 * The `message` keeps the original `API error <status>: ...` format so existing
 * callers that only read `err.message` are unaffected.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, statusText: string, body: unknown, rawBody: string) {
    super(`API error ${status}: ${statusText}${rawBody ? ` - ${rawBody}` : ''}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export const apiFetch = async <T>(
  url: string,
  options: RequestInit = {},
): Promise<T> => {
  const headers = new Headers(options.headers);
  // Fall back to the cookie when the in-memory token isn't set yet — on a direct
  // page load (refresh/bookmark/deep-link) the component's mount fetch can race
  // ahead of AuthProvider hydrating the in-memory token, which would otherwise
  // 401 and render an empty page. Mirrors lib/api/client.ts apiFetch.
  const token = getAccessToken() ?? getCookie(AUTH_TOKEN_COOKIE) ?? null;
  const body = options.body;
  const isFormData =
    typeof FormData !== 'undefined' && body instanceof FormData;

  headers.set('Accept', 'application/json');

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (!isFormData && body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${getApiBaseUrl()}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const rawBody = await response.text();
    let parsed: unknown = rawBody;
    try {
      parsed = rawBody ? JSON.parse(rawBody) : undefined;
    } catch {
      /* non-JSON error body — keep the raw text as the body */
    }
    throw new ApiError(response.status, response.statusText, parsed, rawBody);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
};
